import { useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import RadioInput from 'LuxKit/Radio/RadioInput'
import BodyText from 'LuxKit/Typography/BodyText'
import TourDealPriceToggle from './TourDealPriceToggle'
import Group from 'components/utils/Group'
import useToggle from 'hooks/useToggle'
import { rem } from 'polished'
import styled from 'styled-components'
import { useTourDealSortOptions } from './TourDealFilterContent'

const RadioGroup = styled(Group)`
  padding-inline: ${rem(16)};
`

const SortWrapper = styled(Group)`
  padding-top: ${rem(8)};
  padding-bottom: ${rem(2)};
`

interface Props {
  totalNumberOfOffers: number
  selectedOption: string
  onChange: (value: string) => void
}

function TourDealSortHeader(props: Props) {
  const { totalNumberOfOffers, selectedOption, onChange } = props
  const intl = useIntl()
  const sortOptions = useTourDealSortOptions()
  const [open, toggleSort, _, closeSort] = useToggle()

  const triggerRef = useRef<HTMLButtonElement>(null)
  const selected = useMemo(() =>
    sortOptions.find(o => o.value === selectedOption) ?? sortOptions[0],
  [selectedOption, sortOptions])

  const onSortChange = (value: string) => {
    onChange(value)
    closeSort()
  }

  if (totalNumberOfOffers <= 0) {
    return null
  }

  return (
    <SortWrapper direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
      <BodyText variant="large">
        {intl.formatMessage(
          {
            id: 'TRLRhlZ7y8bNmESsiux5',
            defaultMessage: 'Showing {count, plural, one {# tour} other {# tours}}',
            description: 'Tour count display in tour deals sort header on the tour page',
          },
          { count: totalNumberOfOffers },
        )}
      </BodyText>
      <Group direction="horizontal" gap={8}>
        <AssistChip
          variant="filled"
          size="medium"
          onClick={toggleSort}
          endIcon={<DropdownChevron open={open} />}
          ref={triggerRef}
        >
          {intl.formatMessage(
            {
              id: 'lE-AaAQMl-CkLS4uyM1C',
              defaultMessage: 'Sort: {sortOption}',
              description: 'Sort chip label with selected sort option in tour deals header',
            },
            { sortOption: selected?.label },
          )}
        </AssistChip>
        <DropdownList
          size="S"
          open={open}
          onClose={closeSort}
          anchorRef={triggerRef}
          triggerRef={triggerRef}
          data-testid="sort-options-dropdown"
        >
          <RadioGroup direction="vertical" gap={16}>
            {sortOptions.map(option => <RadioInput
              key={option.value}
              checked={selected?.value === option.value}
              onChange={() => onSortChange(option.value)}
              name="tourDealSortHeader"
              value={option.value}
            >
              {option.label}
            </RadioInput>)}
          </RadioGroup>
        </DropdownList>
        <TourDealPriceToggle />
      </Group>
    </SortWrapper>
  )
}

export default TourDealSortHeader
